import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, MapPin, Phone, Mail, Globe, Dog, Baby, Camera, ChevronDown, Menu, X } from 'lucide-react';
import { API } from '@/utils/api';
import { getImageUrl } from '@/utils/imageUrl';
import { trackPageView } from '@/hooks/usePageViews';
import { useTranslation } from 'react-i18next';

/* ── Load elegant fonts ── */
const FONT_LINK = "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=Cormorant+Garamond:wght@400;500;600;700&family=Lato:wght@300;400;700&display=swap";
function useGoogleFonts() {
  useEffect(() => {
    if (!document.querySelector(`link[href="${FONT_LINK}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = FONT_LINK;
      document.head.appendChild(link);
    }
  }, []);
}

const heading = "'Playfair Display', 'Cormorant Garamond', Georgia, serif";
const body = "'Lato', 'DM Sans', sans-serif";

/* ── Section Heading (centered, gold subtitle + serif heading + line) ── */
function SectionHeading({ subtitle, title, primary, text, muted }) {
  return (
    <div style={{ textAlign: 'center', marginBottom: 40 }}>
      {subtitle && (
        <p style={{ color: primary, fontSize: 12, fontWeight: 600, letterSpacing: '0.18em', textTransform: 'uppercase', fontFamily: body, marginBottom: 10 }}>
          {subtitle}
        </p>
      )}
      <h2 style={{ color: text, fontSize: 34, fontWeight: 600, fontFamily: heading, lineHeight: 1.2, margin: 0, letterSpacing: '-0.01em' }}>
        {title}
      </h2>
      <div style={{ width: 50, height: 2, background: primary, margin: '16px auto 0', borderRadius: 2 }} />
    </div>
  );
}

/* ── NavDropdown ── */
function NavDropdown({ label, items, muted, text, isDark, primary }) {
  const [open, setOpen] = useState(false);
  const dropBg = isDark ? '#1a2340' : '#FFFFFF';
  const border = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';
  return (
    <div style={{ position: 'relative' }} onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <span style={{ color: muted, fontSize: 13, fontWeight: 400, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontFamily: body, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
        {label} <ChevronDown size={11} style={{ transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0)' }} />
      </span>
      {open && (
        <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 8, background: dropBg, border: `1px solid ${border}`, borderRadius: 12, padding: '6px 0', minWidth: 220, boxShadow: '0 12px 40px rgba(0,0,0,0.12)', zIndex: 100 }}>
          {items.map((item, i) => (
            <a key={i} href={`#${item.id}`}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', color: text, fontSize: 14, fontWeight: 400, textDecoration: 'none', fontFamily: body, transition: 'background 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              {item.photo ? (
                <img src={item.photo} alt={item.name} style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'cover' }} />
              ) : (
                <div style={{ width: 36, height: 36, borderRadius: 8, background: isDark ? '#0B0F1A' : '#F0EDE8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {item.type === 'dog' ? <Dog size={14} color={muted} /> : <Baby size={14} color={muted} />}
                </div>
              )}
              <div>
                <div style={{ lineHeight: 1.3 }}>{item.name}</div>
                {item.breed && <div style={{ color: muted, fontSize: 12, marginTop: 2 }}>{item.breed}</div>}
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PublicPage() {
  const { t } = useTranslation();
  useGoogleFonts();
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/s/${slug}`)
      .then(res => { setData(res.data); trackPageView('page', slug); })
      .catch(() => setError('Strona nie znaleziona'))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#0B0F1A]">
      <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
    </div>
  );

  if (error || !data) return (
    <div className="min-h-screen flex items-center justify-center bg-[#0B0F1A]" data-testid="public-page-404">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-white mb-3">404</h1>
        <p className="text-gray-400">{error || 'Strona nie znaleziona'}</p>
      </div>
    </div>
  );

  const { page, profile, dogs, litters, seo } = data;
  const { theme = {}, sections = [] } = page;
  const isDark = theme.background === 'dark';
  const primary = theme.primary_color || '#C8A455';
  const bg = isDark ? '#0B0F1A' : '#FAFAF8';
  const text = isDark ? '#FFFFFF' : '#1A1A1A';
  const muted = isDark ? 'rgba(255,255,255,0.5)' : 'rgba(26,26,26,0.45)';
  const cardBg = isDark ? 'rgba(16,24,43,0.6)' : 'rgba(0,0,0,0.02)';
  const borderCol = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.06)';

  const isMobileView = typeof window !== 'undefined' && window.innerWidth < 768;

  const enabledSections = sections.filter(s => s.enabled).sort((a, b) => a.order - b.order);
  const mainPhoto = getImageUrl(profile.main_photo);
  const breeds = profile.breeds || [];
  const gallery = profile.gallery || [];

  const navLinks = enabledSections.filter(s => ['about', 'offer', 'gallery', 'contact'].includes(s.id));

  return (
    <>
      {seo && <title>{seo.title}</title>}

      <div style={{ background: bg, fontFamily: body, minHeight: '100vh', color: text }} data-testid="public-page">

        {/* ── NAVIGATION ── */}
        <nav style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 24px', borderBottom: `1px solid ${borderCol}`,
          position: 'sticky', top: 0, zIndex: 50, background: bg,
          backdropFilter: 'blur(12px)'
        }} data-testid="public-nav">
          <a href="#" style={{ color: text, fontSize: 20, fontWeight: 600, fontFamily: heading, textDecoration: 'none', letterSpacing: '0.02em' }}>
            {profile.business_name || 'Hodowla'}
          </a>

          {/* Desktop nav */}
          <div className="hidden md:flex" style={{ alignItems: 'center', gap: 28 }}>
            {navLinks.map(s => (
              <a key={s.id} href={`#${s.id}`} style={{ color: muted, fontSize: 12, fontWeight: 400, textDecoration: 'none', fontFamily: body, letterSpacing: '0.08em', textTransform: 'uppercase', transition: 'color 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.color = text}
                onMouseLeave={e => e.currentTarget.style.color = muted}
              >{s.label}</a>
            ))}
            {dogs.length > 0 && (
              <NavDropdown label="Psy" muted={muted} text={text} isDark={isDark} primary={primary}
                items={dogs.map(d => ({ id: `dog-${d.id}`, name: d.name, breed: d.breed, photo: getImageUrl(d.main_photo), type: 'dog' }))} />
            )}
            {litters.length > 0 && (
              <NavDropdown label="Mioty" muted={muted} text={text} isDark={isDark} primary={primary}
                items={litters.map(l => ({ id: `litter-${l.id}`, name: l.name, breed: l.breed, photo: getImageUrl(l.cover_image), type: 'litter' }))} />
            )}
            {(profile.services || []).length > 0 && (
              <NavDropdown label="Usługi" muted={muted} text={text} isDark={isDark} primary={primary}
                items={(profile.services || []).map((s, i) => ({ id: `service-${i}`, name: s.name || s, breed: s.price || '', photo: null, type: 'service' }))} />
            )}
          </div>

          {/* Mobile hamburger */}
          <button className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: muted, padding: 4 }}>
            {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </nav>

        {/* Mobile dropdown menu */}
        {mobileMenuOpen && (
          <div className="md:hidden" style={{ position: 'sticky', top: 56, zIndex: 49, background: bg, borderBottom: `1px solid ${borderCol}`, padding: '16px 24px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {navLinks.map(s => (
                <a key={s.id} href={`#${s.id}`} onClick={() => setMobileMenuOpen(false)} style={{ color: text, fontSize: 14, fontWeight: 500, textDecoration: 'none', fontFamily: body }}>{s.label}</a>
              ))}
              {dogs.length > 0 && <a href="#dogs" onClick={() => setMobileMenuOpen(false)} style={{ color: text, fontSize: 14, fontWeight: 500, textDecoration: 'none', fontFamily: body }}>Psy</a>}
              {litters.length > 0 && <a href="#litters" onClick={() => setMobileMenuOpen(false)} style={{ color: text, fontSize: 14, fontWeight: 500, textDecoration: 'none', fontFamily: body }}>Mioty</a>}
            </div>
          </div>
        )}

        {enabledSections.map(section => {
          switch (section.id) {

            /* ── HERO ── */
            case 'hero':
              return (
                <div key="hero" style={{ position: 'relative', minHeight: 420 }}>
                  <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
                    {mainPhoto
                      ? <img src={mainPhoto} alt={profile.business_name} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 30%' }} />
                      : <div style={{ width: '100%', height: '100%', background: isDark ? '#0B0F1A' : '#E8E4DF' }} />
                    }
                  </div>
                  <div style={{ position: 'absolute', inset: 0, background: isDark
                    ? 'linear-gradient(to top, rgba(11,15,26,0.88) 0%, rgba(11,15,26,0.35) 55%, rgba(11,15,26,0.1) 100%)'
                    : 'linear-gradient(to top, rgba(250,250,248,0.95) 0%, rgba(250,250,248,0.45) 45%, rgba(250,250,248,0.05) 100%)'
                  }} />
                  <div style={{ position: 'relative', zIndex: 2, padding: '80px 24px 56px', maxWidth: 900, margin: '0 auto' }} className="md:!px-12 md:!py-[100px]">
                    {breeds.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
                        {breeds.map((b, i) => (
                          <span key={i} style={{ padding: '4px 14px', background: `${primary}18`, border: `1px solid ${primary}30`, borderRadius: 100, color: primary, fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', fontFamily: body }}>{b}</span>
                        ))}
                      </div>
                    )}
                    <h1 style={{ color: text, fontSize: 56, fontWeight: 700, margin: 0, lineHeight: 1.05, letterSpacing: '-0.02em', fontFamily: heading }}>
                      {profile.business_name || 'Hodowla'}
                    </h1>
                    <p style={{ color: muted, fontSize: 17, marginTop: 16, fontWeight: 300, lineHeight: 1.7, maxWidth: 540, fontFamily: body }}>
                      {profile.description?.slice(0, 140) || 'Profesjonalna hodowla z pasją i doświadczeniem'}
                      {(profile.description?.length || 0) > 140 ? '...' : ''}
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 28 }}>
                      <a href="#contact" style={{ background: primary, color: '#fff', padding: '14px 36px', borderRadius: 8, fontSize: 14, fontWeight: 600, textDecoration: 'none', fontFamily: body, letterSpacing: '0.04em', transition: 'opacity 0.2s' }}>
                        {profile.business_type === 'breeder' ? 'Zapytaj o szczenieta' : 'Napisz do nas'}
                      </a>
                      {profile.city && (
                        <span style={{ color: muted, fontSize: 14, display: 'flex', alignItems: 'center', gap: 6, fontFamily: body }}>
                          <MapPin size={15} /> {profile.city}{profile.voivodeship ? `, ${profile.voivodeship}` : ''}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );

            /* ── O NAS ── */
            case 'about':
              return profile.description ? (
                <div key="about" id="about" style={{ maxWidth: 800, margin: '0 auto', padding: '48px 24px 40px' }}>
                  <SectionHeading subtitle="Poznaj nas" title="O nas" primary={primary} text={text} muted={muted} />
                  <p style={{ color: muted, fontSize: 16, lineHeight: 2, fontFamily: body, fontWeight: 300, textAlign: 'center' }}>
                    {profile.description}
                  </p>
                  {profile.experience && (
                    <p style={{ color: muted, fontSize: 15, marginTop: 24, fontStyle: 'italic', opacity: 0.7, textAlign: 'center', fontFamily: body, lineHeight: 1.8, fontWeight: 300 }}>
                      {profile.experience}
                    </p>
                  )}
                </div>
              ) : null;

            /* ── OFERTA ── */
            case 'offer':
              return (profile.offer || profile.offer_description) ? (
                <div key="offer" id="offer" style={{ maxWidth: 800, margin: '0 auto', padding: '0 24px 48px' }}>
                  <SectionHeading subtitle="Co oferujemy" title="Oferta" primary={primary} text={text} muted={muted} />
                  <p style={{ color: muted, fontSize: 16, lineHeight: 2, fontFamily: body, fontWeight: 300, textAlign: 'center' }}>
                    {profile.offer || profile.offer_description}
                  </p>
                </div>
              ) : null;

            /* ── PSY ── */
            case 'dogs':
              return dogs.length > 0 ? (
                <div key="dogs" id="dogs" style={{ maxWidth: 900, margin: '0 auto', padding: '0 24px 48px' }}>
                  <SectionHeading subtitle="Nasze psy" title={`Psy (${dogs.length})`} primary={primary} text={text} muted={muted} />
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 20 }}>
                    {dogs.map(dog => (
                      <div key={dog.id} id={`dog-${dog.id}`} style={{ background: cardBg, border: `1px solid ${borderCol}`, borderRadius: 16, overflow: 'hidden', scrollMarginTop: 80, transition: 'transform 0.2s, box-shadow 0.2s' }}>
                        {dog.main_photo ? (
                          <img src={getImageUrl(dog.main_photo)} alt={dog.name} style={{ width: '100%', height: 180, objectFit: 'cover' }} />
                        ) : (
                          <div style={{ height: 180, background: isDark ? '#1a2340' : '#E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Dog size={32} style={{ color: muted }} />
                          </div>
                        )}
                        <div style={{ padding: 16 }}>
                          <p style={{ color: text, fontWeight: 600, fontSize: 16, fontFamily: heading }}>{dog.name}</p>
                          <p style={{ color: muted, fontSize: 13, marginTop: 4, fontFamily: body }}>{dog.breed}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null;

            /* ── MIOTY ── */
            case 'litters':
              return litters.length > 0 ? (
                <div key="litters" id="litters" style={{ maxWidth: 900, margin: '0 auto', padding: '0 24px 48px' }}>
                  <SectionHeading subtitle="Nasze mioty" title={`Mioty (${litters.length})`} primary={primary} text={text} muted={muted} />
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 20 }}>
                    {litters.map(litter => (
                      <div key={litter.id} id={`litter-${litter.id}`} style={{ background: cardBg, border: `1px solid ${borderCol}`, borderRadius: 16, overflow: 'hidden', scrollMarginTop: 80 }}>
                        {litter.cover_image ? (
                          <img src={getImageUrl(litter.cover_image)} alt={litter.name} style={{ width: '100%', height: 180, objectFit: 'cover' }} />
                        ) : (
                          <div style={{ height: 180, background: isDark ? '#1a2340' : '#E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Baby size={32} style={{ color: muted }} />
                          </div>
                        )}
                        <div style={{ padding: 16 }}>
                          <p style={{ color: text, fontWeight: 600, fontSize: 16, fontFamily: heading }}>{litter.name}</p>
                          <p style={{ color: muted, fontSize: 13, marginTop: 4, fontFamily: body }}>{litter.breed}</p>
                          {litter.available_count > 0 && (
                            <span style={{ display: 'inline-block', marginTop: 8, padding: '3px 10px', background: `${primary}20`, color: primary, borderRadius: 8, fontSize: 11, fontWeight: 600, fontFamily: body }}>
                              Dostępne: {litter.available_count}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null;

            /* ── GALERIA ── */
            case 'gallery':
              return gallery.length > 0 ? (
                <div key="gallery" id="gallery" style={{ maxWidth: 900, margin: '0 auto', padding: '0 24px 48px' }}>
                  <SectionHeading subtitle="Zdjęcia" title="Galeria" primary={primary} text={text} muted={muted} />
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14 }}>
                    {gallery.map((img, i) => (
                      <img key={i} src={getImageUrl(img)} alt={`Galeria ${i + 1}`} style={{ width: '100%', height: 200, objectFit: 'cover', borderRadius: 12 }} />
                    ))}
                  </div>
                </div>
              ) : null;

            /* ── KONTAKT ── */
            case 'contact':
              return (
                <div key="contact" id="contact" style={{ maxWidth: 800, margin: '0 auto', padding: '0 24px 48px' }}>
                  <SectionHeading subtitle="Skontaktuj się" title="Kontakt" primary={primary} text={text} muted={muted} />
                  <div style={{ background: cardBg, border: `1px solid ${borderCol}`, borderRadius: 16, padding: 32, display: 'flex', flexWrap: 'wrap', gap: 28, justifyContent: 'center' }}>
                    {profile.phone && (
                      <a href={`tel:${profile.phone}`} style={{ color: primary, display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', fontSize: 15, fontFamily: body }}>
                        <Phone size={16} /> {profile.phone}
                      </a>
                    )}
                    {profile.email && (
                      <a href={`mailto:${profile.email}`} style={{ color: primary, display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', fontSize: 15, fontFamily: body }}>
                        <Mail size={16} /> {profile.email}
                      </a>
                    )}
                    {profile.social_website && (
                      <a href={profile.social_website} target="_blank" rel="noopener noreferrer" style={{ color: primary, display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', fontSize: 15, fontFamily: body }}>
                        <Globe size={16} /> Strona WWW
                      </a>
                    )}
                    {!profile.phone && !profile.email && !profile.social_website && (
                      <p style={{ color: muted, fontSize: 14, fontFamily: body }}>Brak danych kontaktowych</p>
                    )}
                  </div>
                </div>
              );

            default:
              return null;
          }
        })}

        {/* ── FOOTER with ApisAI branding ── */}
        <footer style={{ borderTop: `1px solid ${borderCol}`, padding: '24px', background: isDark ? 'rgba(0,0,0,0.3)' : '#FFFFFF' }} className="md:!px-12">
          <div style={{ maxWidth: 900, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
            <a href="https://apisai.pl" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
              <img src="/logo.svg" alt="ApisAI" style={{ width: 28, height: 28 }} onError={e => { e.target.style.display = 'none'; }} />
              <span style={{ color: text, fontSize: 16, fontWeight: 700, fontFamily: heading, letterSpacing: '0.02em' }}>APISAI</span>
            </a>
            <p style={{ color: muted, fontSize: 13, fontFamily: body, fontWeight: 300, margin: 0, textAlign: 'center', flex: 1 }}>
              Zamień swoją ofertę w klientów
            </p>
            <a href="https://apisai.pl" target="_blank" rel="noopener noreferrer"
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 24px', background: primary, color: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 600, textDecoration: 'none', fontFamily: body, letterSpacing: '0.03em', transition: 'opacity 0.2s' }}>
              Sprawdź ApisAI.pl
            </a>
          </div>
        </footer>
      </div>
    </>
  );
}
